import React from 'react';
import type { ApiPeriod, PeriodRequest, ServedPeriod } from './periods';
import type { AVERAGE_UNAVAILABLE } from './averages';
import type { AnalyticsStore } from '~stores/analytics';
import type {
    BreakdownAxis,
    RegionGrain,
    EnvelopeCaveat,
    DegradationCause,
} from '~stores/kpi-envelope';

/**
 * Domain types for the Overview KPI catalog (real-data, multi-form).
 *
 * A card is a fully serializable spec (source + metric + form + period). The
 * source layer turns it into a canonical `KpiCardData`, which the form renderers
 * consume; nothing above the source sees a source's raw field names.
 */

/** Card footprint, mapped to a grid-column span by the shell. */
export type KpiSize = 'sm' | 'md' | 'lg';
export const KPI_SIZES = ['sm', 'md', 'lg'] as const;

/** Visual form a card can take. */
export type KpiForm =
    | 'number'
    | 'rate'
    | 'line'
    | 'pie'
    | 'bars'
    | 'columns'
    | 'split'
    | 'downloads'
    | 'multiline'
    | 'trio'
    | 'rate-trio';
export const KPI_FORMS = [
    'number',
    'rate',
    'line',
    'pie',
    'bars',
    'columns',
    'split',
    'downloads',
    'multiline',
    'trio',
    'rate-trio',
] as const;

/** Data sources the catalog can pull from. */
export type KpiSourceId =
    | 'age-gating'
    | 'age-brackets'
    | 'profile-creation-conversion'
    | 'coupon-activation'
    | 'app-downloads'
    | 'app-downloads-total'
    | 'carwash-engagement'
    | 'total-users'
    | 'feature-active-users'
    | 'payment-methods-added'
    | 'new-users'
    | 'new-users-rpp'
    | 'engagement'
    | 'engagement-rpp'
    | 'opt-ins'
    | 'opt-ins-rpp'
    | 'game-engagement-rpp'
    | 'technical-health'
    | 'engagement-drivers-rpp'
    | 'six12-purchases'
    | 'coupon-issued'
    | 'coupon-issued-rpp'
    | 'coupon-redeemed'
    | 'coupon-redeemed-rpp'
    | 'coupon-redeemed-age-restricted'
    | 'coupon-top-redeemed'
    | 'onboarding-state'
    | 'placeholder'
    | 'churn'
    | 'login-activity'
    | 'login-activity-rpp'
    | 'login-retention'
    | 'login-retention-rpp'
    | 'campaign-members'
    | 'campaign-purchases'
    | 'campaign-coupons-issued'
    | 'campaign-enrollment-funnel'
    | 'campaign-visits'
    | 'campaign-visits-ledger'
    | 'campaign-milestones'
    | 'campaign-contactability'
    | 'campaign-coupons-member'
    | 'campaign-segment-member'
    | 'campaign-nhl-coins-collection'
    | 'campaign-nhl-coins-popularity'
    | 'inactivity-return'
    | 'players-31doc'
    | 'door-openings-31doc'
    | 'coupons-31doc'
    | 'purchases-31doc'
    | 'engagement-drivers-31doc';

/**
 * The customer groups `campaign-segment-member` narrows its population to, a
 * partition: Couche-Tard (a Québec account) split on the fuel-tester flag,
 * Circle K (every other account) split on age verification.
 */
export type KpiSegment =
    | 'ck-age-verified'
    | 'ck-not-age-verified'
    | 'ct-not-fuel-tester'
    | 'ct-fuel-tester';

/** Metric identifiers the catalog supports. A union (not free `string`) so a
 * typo in a card spec is a compile error instead of a silent fallback. Extend
 * it as sources add metrics. */
export type KpiMetric =
    | 'appDownloadsTotal'
    | 'newUsers'
    | 'ageGatedUsers'
    | 'ageGatedRate'
    // `newUsers` minus `ageGatedUsers`, differenced by the endpoint: the two
    // count the same cohort, so the remainder is the unverified part of it.
    | 'unverifiedNewUsers'
    | 'registrations'
    | 'installs'
    | 'conversionRate'
    | 'registered'
    | 'profileCompleted'
    | 'claimed'
    | 'available'
    | 'redemptionRate'
    // Bounded by the reissue date, not the created date `claimed` uses. Not a
    // subset of `claimed`; the two must never be differenced.
    | 'reissued'
    | 'total'
    | 'dailyActiveUsers'
    | 'weeklyActiveUsers'
    | 'monthlyActiveUsers'
    | 'activeUsers'
    | 'activeUsersRpp'
    | 'logins'
    | 'loginsRpp'
    | 'returningUserRate'
    | 'returningUserRateRpp'
    | 'averageActiveDaysPerUser'
    | 'averageActiveDaysPerUserRpp'
    | 'sessionsPerUser'
    | 'repeatVisitRate'
    | 'avgSessionLengthSeconds'
    // RPP-cohort siblings, read as single-value cards on the RPP dashboard once
    // Full app was dropped from the overall-performance / business-impact cards.
    | 'weeklyActiveUsersRpp'
    | 'dailyActiveUsersRpp'
    | 'newUsersRpp'
    | 'sessionsPerUserRpp'
    | 'repeatVisitRateRpp'
    | 'emailOptIns'
    | 'gameSessionsOpened'
    | 'gamesPlayed'
    | 'avgPlaysPerUser'
    | 'gameCompletionRate'
    | 'gameDropOffRate'
    | 'errorRate'
    | 'failedSessions'
    | 'badgeEarnRate'
    | 'bonusCompletionRate'
    | 'emailOptInsRpp'
    | 'smsOptInsRpp'
    | 'individuals'
    | 'transactions'
    | 'revenue'
    | 'avgSpend'
    | 'itemsPerBasket'
    // Also served campaign-scoped by `campaign-coupons-issued`, and again by
    // `campaign-coupons-member` for the milestone ladder: the two campaign-scoped
    // readings are asserted equal by an API test. This unscoped call counts
    // promotions with no campaign, not all of them, so it is disjoint from both.
    | 'couponsIssued'
    // RPP grants (`is_for_rpp` promotions), attributed by the RPP registration
    // province, including coupons released unredeemed at expiry. Never sums
    // with `couponsIssued`: neither nests in the other.
    | 'couponsIssuedRpp'
    // The redemption counterpart of `couponsIssuedRpp`: same window, same
    // province source, so the two are a funnel. NOT `coupon-activation`'s
    // `claimed`, which covers the non-RPP promotions this metric excludes.
    | 'couponsRedeemedRpp'
    // Whole-app redemptions across every promotion, the scalar the top-redeemed
    // ranking sits under. `couponsRedeemedRpp` above is a subset of it, on a
    // different province source; never sum the two. Also reused campaign-scoped
    // by `campaign-coupons-member`, on the same terms as `couponsIssued` above.
    | 'couponsRedeemed'
    | 'accountDeletions'
    | 'accountSwaps'
    | 'churnRate'
    // Two rates over one base, the recently active users: those who fell silent
    // for the inactivity threshold, and those who logged in after such a
    // stretch. Not comparable with `churnRate`, which counts over signups.
    | 'wentInactiveRate'
    | 'cameBackRate'
    // Emitted by `onboarding-state` and declared by its source, but missing
    // from this union until the source's metric ids were bound to it: the old
    // `M extends string` let a source name an id the catalog did not know, so
    // no card could ever reference this one and nothing said why.
    | 'ageVerified'
    | 'phoneVerified'
    | 'segmentation'
    | 'permission'
    | 'complete'
    | 'washCodesGenerated'
    | 'beaconsGenerated'
    | 'beaconsRequested'
    | 'totalUsers'
    | 'featureActiveUsers'
    | 'paymentMethodsAdded'
    // Road to Rewards membership counts. Each counts who became so INSIDE the
    // window asked for, so the three nest only on a campaign-to-date window.
    | 'enrolledMembers'
    | 'uniqueMembers'
    | 'activeMembers'
    // Road to Rewards purchase counts.
    | 'storeVisits'
    | 'spend'
    | 'spendingMembers'
    | 'avgSpendPerMember'
    | 'avgSpendPerTransaction'
    | 'avgUnitsPerTransaction'
    // Road to Rewards visit counts. `repeatVisitRate` above (declared for
    // `engagement`/`login-retention`) is reused rather than duplicated here.
    | 'avgVisitsPerMember'
    | 'returningMembers'
    // Whole-campaign visit ledger: every LIFT row Six12 sent, any phone,
    // enrolled or not. Shares no denominator with the enrolled-member counts
    // above (`avgVisitsPerMember`, `returningMembers`) or with `storeVisits`.
    | 'visitsReceived'
    | 'visitsCounted'
    | 'rejectedUnderMinSpend'
    | 'rejectedGapTooShort'
    | 'rejectedDailyCap'
    | 'revenueReceived'
    | 'revenueCounted'
    // The visit-milestone ladder's own cohort scalar: members with at least
    // one posted COUNTED visit, which is milestone 1's own row and can read
    // lower than campaign-members' enrolledMembers while a bonus is pending.
    | 'membersOnLadder'
    // Milestone ladder coupon counts, from `campaign-coupons-member`. The member
    // counts are distinct phones, resolved the same way as the visit/purchase
    // counts above, so a province filter can drop a shared-phone duplicate
    // from one side without moving the other by the same amount.
    | 'issuedMembers'
    | 'redeemedMembers'
    | 'expiredCoupons'
    // Cohort rates, also from `campaign-coupons-member`: among the coupons (or
    // members) a window's ISSUANCE side covers, the share redeemed since. Not
    // derivable from `couponsRedeemed`/`redeemedMembers` above, whose window
    // scopes the REDEMPTION side instead.
    | 'couponRedemptionRate'
    | 'memberRedemptionRate'
    // One customer group's active members over the campaign's own, from
    // `campaign-segment-member`. The groups overlap, so the four never sum to
    // 1 and nothing may normalize them.
    | 'shareOfCampaign'
    // Road to Rewards contactability: reachable members over all enrolled
    // members, as a ratio. `phoneVerified` above (declared for
    // `onboarding-state`) is reused rather than duplicated.
    | 'pushReachable'
    | 'smsReachable'
    // NHL Coins booklets: players holding a coin, holding all 20, and their average.
    | 'collectors'
    | 'completeCollectors'
    | 'avgCoinsPerCollector'
    // NHL Coins popularity: coins checked across the booklets, split by coin.
    | 'coinsHeld'
    // 31DOC dashboard: one edition's players, door openings and coupons.
    | 'activeUsers31doc'
    | 'weeklyActiveUsers31doc'
    | 'dailyActiveUsers31doc'
    | 'newPlayers31doc'
    | 'repeatVisitRate31doc'
    | 'doorsOpened31doc'
    | 'doorPlayers31doc'
    | 'openingsPerPlayer31doc'
    | 'couponsIssued31doc'
    | 'couponsRedeemed31doc'
    // The age-brackets cohort scalar: accounts created in the selected
    // period, the same population the ageBracket axis rows sum to.
    | 'accounts'
    // The `placeholder` source's synthetic id. Not an API metric: that source
    // fabricates data for unbuilt cards. It is here because it appears in a
    // descriptor, and the union is now the domain of every declared metric id.
    | 'sample';

/** The presets a card can pin, all resolved by the API. */
export type PeriodPreset = Exclude<ApiPeriod, 'today' | 'campaign'>;

export const PERIOD_PRESETS: readonly PeriodPreset[] = [
    '7d',
    '30d',
    '90d',
    'last-week',
    'last-month',
];

/** Envelope breakdown axes a chart card can render. A union (not free `string`)
 * so a typo in a card spec is a compile error instead of a silent error tile.
 * Narrowed from `BreakdownAxis` rather than written out, so an axis the envelope
 * renames or drops stops compiling here instead of drifting. Widen the `Extract`
 * as cards learn to render more axes.
 *
 * `businessUnit` is a region axis, and region axes were excluded when this type
 * was written on the grounds that region is a page filter. That reasoning holds
 * for filtering but not for shape: "show me Quebec" and "show me how the total
 * splits across the four business units" are different questions, and the second
 * is what the region axes were added to the envelope to answer. `province` rows
 * enumerate all thirteen codes plus `UNKNOWN` whether or not they carry a value,
 * which is a list, not a chart: draw it as `bars`. */
export type KpiBreakdownAxis = Extract<
    BreakdownAxis,
    | 'brand'
    | 'device'
    | 'round'
    | 'badge'
    | 'bonusAction'
    | 'businessUnit'
    | 'province'
    | 'cohort'
    | 'promotion'
    | 'category'
    | 'ageBracket'
    | 'visitMilestone'
    | 'coinCount'
    | 'entryTier'
    | 'coin'
>;

/** A colour family a global Analytics tab themes its charts in, spread over
 * its ramp by `resolveSeriesColors`. */
export type KpiThemePalette = 'blue' | 'pink' | 'orange' | 'brown';

/** Named chart color ramp. `campaign`: orange then red, by position (`pie`: CT
 * red then pale pink). `device`/`app`: `pie`, by label; `milestone`: `columns`.
 * The rest are `KpiThemePalette`. */
export type KpiPalette =
    | 'device'
    | 'app'
    | 'campaign'
    | 'milestone'
    | KpiThemePalette;

/** How a `multiline` card's values read: a plain count, or a 0..100 percent
 * (same scale as `KpiSplitSide`'s `unit: 'percent'`). */
export type KpiMultilineUnit = 'count' | 'percent';

/** A preset or an explicit `YYYY-MM-DD` range. */
export type Period = PeriodPreset | { start: string; end: string };

/**
 * Explicit province list. `[]` = National (no province filter); `['QC']` = a
 * single province. Multiple provinces are sent to the source and filtered server-side.
 */
export type Region = string[];

/**
 * Everything on a card descriptor EXCEPT `source` and `metric`.
 *
 * Those two live in `card-spec.ts` because they are not independent: a card may
 * only name a metric its own source actually serves. Declaring them as two free
 * unions is what let a card point at a real source and a real metric that
 * source does not serve, which shipped once (commit `fce249e1`) and rendered an
 * error tile with every spec green. `card-spec.ts` derives the metric domain
 * per source from `SOURCES` and re-joins them; import `KpiCardSpec` from there.
 * This base exists so that module can build on it without an import cycle.
 */
export interface KpiCardSpecBase {
    title: string;
    /** Muted line beneath the title, describing THIS card's own data (e.g.
     * "Combined view"). Distinct from a `SectionHeader` subtitle, which
     * describes the class of cards a whole row holds, never one card's data. */
    subtitle?: string;
    form: KpiForm;
    /** Omitted = follow the page filter (Model A). A preset/custom value pins the card. */
    period?: Period;
    region?: Region;
    size: KpiSize;
    /** When `false`, suppresses the comparison range for number/rate/split cards. Omitted = trend on. */
    trend?: boolean;
    /** When `true`, the request keeps the comparison range (so it can match a
     * sibling card's request) but the card renders no trend. */
    hideTrend?: boolean;
    /** When `true`, the metric is lower-is-better (e.g. churn): the trend renders
     * with inverted sentiment (a rise is red, a drop is green). Omitted = higher is better. */
    invertTrend?: boolean;
    /** Short description shown in the (i) info popover. */
    description?: string;
    /** For chart forms: which envelope breakdown axis to render. */
    breakdownBy?: KpiBreakdownAxis;
    /** For a truncated-ranking source: how many rows to request (the endpoint's
     * `limit` param), overriding the source's own pinned value. */
    limit?: number;
    /** The campaign this card reads, for a source that serves several;
     * forwarded as the `campaignId` query param. Omitted = the source's own. */
    campaignId?: string;
    /** Color ramp for a chart form; see `KpiPalette`. Omitted = the form's own default. */
    palette?: KpiPalette;
    /** When true, the card resolves a coupon selection before fetching: it loads
     * the coupon list (`useCouponList`), auto-picks the first coupon as the
     * default, and feeds that coupon's id into the request as `promotionId`.
     * Used by the Overview "Coupon activated" card. Slice 5 adds the visible
     * dropdown on top of the same selection state. Omitted for every other card,
     * which is then completely unaffected. */
    couponSelection?: boolean;
    /** Under a coupon selection, a public coupon shows N/A instead of fetching:
     * it is redeemed without a grant, so a grant count has nothing to read. */
    privateCouponsOnly?: boolean;
    /** Under a coupon selection, a reusable coupon shows N/A instead of fetching:
     * it is redeemed in store, so the app records no redemption to read. */
    appTrackedCouponsOnly?: boolean;
    /** For `bars` cards in rate mode: show the shared base in the card title (e.g.
     * `Badges (15,787)`) instead of on every row, so each row reads just `21%`.
     * Omitted = the base rides along on each row as `21% (15,787)`. */
    barBaseInHeader?: boolean;
    /** Inline sample payload for a card on the `placeholder` source: when present,
     * the placeholder source returns it verbatim instead of the global per-form
     * default (see `SAMPLE_DATA` in `sources/placeholder.ts`), so one card can show
     * design-accurate sample values without changing every other placeholder card
     * that shares its form. The shape must match the spec's own `form`. Omitted
     * (the common case) keeps today's behaviour: the global default, unchanged.
     * Ignored by sources other than `placeholder`. */
    sample?: KpiCardData;
    /** `trio` form only: render just the highlighted to-date tile, unlabelled
     * like the stock cards beside it, dropping Weekly avg. and Daily avg.
     * Omitted = the full three-tile row. Ignored by every other form. */
    toDateOnly?: boolean;
    /** `pie` form only: show the first slice's value as a headline above the
     * ring. Ignored by every other form. */
    headlineFromFirstSlice?: boolean;
    /** `pie` form only: stack the legend under the ring at every viewport,
     * instead of beside it (side by side, stacking only under the mobile
     * breakpoint). Independent of `size`: an `sm` pie with this unset keeps
     * the side-by-side layout. Ignored by every other form. */
    legendBelow?: boolean;
    /** `pie` form only: legend beside a smaller ring, for a narrow `sm` card.
     * Takes the place of `withDashboardLook`'s default `legendBelow`. Ignored
     * by every other form. */
    legendBeside?: 'compact';
    /** `bars` form only: renders a full-width filled funnel track instead of
     * the label/track/value list. Ignored by every other form. */
    barsStyle?: 'filled';
    /** `columns` form only: plots each column's share of the visible total
     * (0-100%) instead of its raw count, with a percent y-axis. Ignored by
     * every other form. */
    shareAxis?: boolean;
    /** `line`/`multiline` form only: renders a gradient-filled area instead of
     * plain lines. Ignored by every other form. */
    chartStyle?: 'area';
    /** `columns` form only: prints a column's `subLabel` as a signed day
     * count (e.g. `+1.4d`, `0 d`). Ignored by every other form. */
    subLabelUnit?: 'days';
    /** `number`/`rate` form only: renders the value as a single highlighted
     * boxed tile (`BoxedTileRow`), like RtR's "Total coupons". Ignored by
     * every other form. */
    boxed?: boolean;
}

/** Trend annotation attached to number/rate cards when a compare range is available. */
export interface KpiTrend {
    delta: number;
    /** How to read `delta`, always an ABSOLUTE change in the metric's own unit.
     * `percent` = percentage-points for a rate, rendered `+5.36%` (a rate is a
     * 0..1 share, so the points delta stays within +/-100). `currency` = a
     * dollar change, rendered `-$5.36`. `duration` = a seconds change, rendered
     * `+1m 30s` (matches the duration value it sits under). `count` = a plain
     * number change, rendered `+420` / `-0.33`. */
    unit: 'percent' | 'count' | 'currency' | 'duration';
    direction: 'up' | 'down' | 'flat';
    comparisonLabel: string;
    /** When true, lower is better for this metric (e.g. error rate, failed
     * sessions, churn), so the renderer flips the trend colour: a decrease reads
     * green (good) and an increase red (bad). The arrow still points the real
     * direction of change. Omitted/false = the default (higher is better). */
    invert?: boolean;
}

/**
 * One side of a `split` card: a labelled value (e.g. "RPP" vs "Full app"), its own
 * optional trend, and a mutually-exclusive display hint. `unit: 'percent'` renders
 * the value as `N%`; `isCurrency` renders it as `$N`; otherwise a plain localized
 * count. The union stops `percent` and `isCurrency` from being set together.
 *
 * An `unavailable` side carries no value: a cohort we cannot source yet (e.g. the
 * RPP half of a split card until an RPP breakdown is emitted). The renderer shows a
 * placeholder instead of a number, and no trend.
 */
export type KpiSplitSide =
    | ({
          label: string;
          value: number;
          trend?: KpiTrend;
      } & (
          | { unit: 'percent'; isCurrency?: never }
          | { unit?: never; isCurrency?: boolean }
      ))
    | { label: string; unavailable: true };

/** One `rate-trio` window's own real read: the same numerator/denominator/rate
 * triple a single `rate` card reads, `null` on an empty denominator. */
export interface KpiRateWindow {
    numerator: number;
    denominator: number;
    rate: number | null;
}

/** Canonical per-form data, produced by a source and consumed by a renderer. */
export type KpiCardData =
    | {
          form: 'number';
          value: number;
          isDuration?: boolean;
          isCurrency?: boolean;
          trend?: KpiTrend;
      }
    | {
          form: 'rate';
          numerator: number;
          denominator: number;
          rate: number | null;
          trend?: KpiTrend;
      }
    | { form: 'line'; points: { date: string; value: number }[] }
    | {
          form: 'pie';
          slices: { label: string; value: number }[];
          palette?: KpiPalette;
      }
    | {
          form: 'bars';
          steps: (
              | {
                    label: string;
                    value: number;
                    rate?: number;
                    base?: number;
                }
              /** Declared unavailable for the window (a null scalar), distinct
               * from a real zero: renders with no fill and an N/A readout
               * while its sibling bars render normally. */
              | { label: string; unavailable: true }
          )[];
          /** The shared denominator every step's rate is against, when the
           * source declares one. Carried here rather than read off a step so
           * the header still shows it when every step is unavailable. */
          base?: number;
      }
    | {
          form: 'columns';
          columns: (
              | {
                    label: string;
                    value: number;
                    delta?: number;
                    /** Rate mode (per-round drop-off): the bar's own share as a
                     * 0..1 value, or null to render N/A. Its presence flips the
                     * columns form from retention count bars to absolute
                     * percentage bars. */
                    rate?: number | null;
                    /** Share of a configured denominator column, 0..1, printed
                     * BESIDE the count (`38 · 100%`) rather than replacing it.
                     * Deliberately not `rate`: `rate` flips the form into
                     * percentage-only bars with a drop-off tooltip, and this
                     * reading wants both numbers, so it stays in retention
                     * mode. */
                    share?: number;
                    /** Rate-mode hover breakdown: the raw per-round funnel
                     * counts, so the bar's percentage stays legible. */
                    tooltip?: {
                        reached: number;
                        dropped: number;
                        won: number;
                    };
                    /** Optional numeric sub-label rendered under the bar (e.g.
                     * an average day count). Absent everywhere, no sub-label
                     * row renders; absent here only, this bar reads N/A. */
                    subLabel?: number;
                    /** Renders under the `milestone` palette's emphasis fill
                     * (dark blue) rather than its base one. */
                    emphasis?: boolean;
                }
              /** Declared unavailable for the window (a null scalar), distinct
               * from a real zero: renders as an N/A label over no visible
               * column, while its sibling columns render normally. */
              | { label: string; unavailable: true }
          )[];
          headline?: { rate: number | null; trend?: KpiTrend };
      }
    | {
          form: 'split';
          left: KpiSplitSide;
          right: KpiSplitSide;
      }
    | {
          form: 'downloads';
          /**
           * Inclusive x-axis bounds, ISO `YYYY-MM-DD`: the selected period,
           * clipped at today and shortened to the longest window the chart can
           * draw. Carried explicitly rather than derived from the points,
           * because the axis has to span the whole window even where no series
           * reaches: the distance between a line's last point and `range.end`
           * is exactly how far behind that platform's source is.
           *
           * A renderer draws THIS range and nothing wider.
           */
          range: { start: string; end: string };
          /**
           * One named daily series per platform that answered, in a stable
           * order. `points` holds only the days that platform actually
           * reported, so a day missing from it means "not reported yet" and a
           * renderer must leave it as a gap: an absent day is NOT a zero. A
           * reported zero is a real zero and is present in `points`.
           *
           * `total` is that platform's own period total as the API served it,
           * never a sum of `points` (the two can differ, and the API's figure
           * is the authoritative one).
           *
           * `dataCompleteThrough` is the platform's freshness as the API
           * declared it, not as the points imply it. The two normally agree,
           * but only the declared date survives a platform that reports no rows
           * at all: empty `points` WITH a date says "complete through the 14th,
           * nothing to show", where empty `points` alone says nothing. Absent
           * when the envelope declared none.
           */
          series: {
              label: string;
              total: number;
              dataCompleteThrough?: string;
              points: { date: string; value: number }[];
          }[];
          /** One entry per platform with no line, each carrying the cause the
           * renderer picks an icon from. Deduped on `reason`, so both platforms
           * failing the same way still reads once. */
          missing?: { cause?: DegradationCause; reason: string }[];
      }
    | {
          form: 'trio';
          /** A campaign total the page Period never moves, and the two rates
           * over the period it does move. All three follow Region. `weekly`
           * reads `AVERAGE_UNAVAILABLE` under a week, and both rates read it
           * on a window that cannot be measured (see `averages.ts`). */
          toDate: number;
          weekly: number | typeof AVERAGE_UNAVAILABLE;
          daily: number | typeof AVERAGE_UNAVAILABLE;
          isCurrency?: boolean;
          /** All three figures are a 0..1 rate, rendered through the shared
           * percent formatter, mirroring `isCurrency`'s `$` prefix. Unenforced:
           * if a source set both, this one wins. */
          isPercent?: boolean;
      }
    | {
          form: 'rate-trio';
          /** Three REAL rate reads, never one figure divided down from
           * another: the campaign-to-date window, the last
           * `WEEKLY_THRESHOLD_DAYS` days of the selected period, and its last
           * day. `weekly`/`daily` read `AVERAGE_UNAVAILABLE` when their own
           * window could not be read at all (including `weekly` on a selected
           * period shorter than a week, see `averages.ts`); a window that WAS
           * read keeps its own `rate: null` for an empty denominator, never
           * collapsed to this sentinel or to a false 0%.
           */
          toDate: KpiRateWindow;
          weekly: KpiRateWindow | typeof AVERAGE_UNAVAILABLE;
          daily: KpiRateWindow | typeof AVERAGE_UNAVAILABLE;
          /** The unit the three windows read in, same contract as `trio`'s
           * pair above: a ratio here is not always a percentage, it can be
           * dollars or units per transaction. */
          isCurrency?: boolean;
          isPercent?: boolean;
      }
    | {
          form: 'multiline';
          /** Drawn in full, same contract as `downloads.range` above. */
          range: { start: string; end: string };
          /** Set only when the period figures stop short of `range.end`: every
           * `value` below covers `range.start` through this date, while the
           * chart still draws the whole range. */
          completeThrough?: string;
          unit: KpiMultilineUnit;
          series: {
              label: string;
              /** `value`, not `total`: a rate series cannot be summed, so
               * each source decides what its own period figure means. */
              value: number | null;
              points: { date: string; value: number | null }[];
          }[];
      };

/** Per-card fetch state. `pending` = data shape not yet available.
 * `unavailable` = no data can be shown at all; `cause` says why in one token
 * the renderer branches on, absent only while an un-migrated envelope is in
 * flight. */
export type KpiCardState =
    | {
          status: 'loading';
          /** The last `ready` data, with the period and compare it answered,
           * carried through consecutive re-fetches so the card keeps its content.
           * Absent on first load or after a re-fetch following a non-ready state. */
          stale?: KpiStaleCard;
      }
    | {
          status: 'ready';
          data: KpiCardData;
          caveats?: EnvelopeCaveat[];
          /** When the envelope's figures were computed (ISO). Absent when it
           * carries none. */
          computedAt?: string;
          served?: ServedPeriod;
      }
    | { status: 'unavailable'; reason: string; cause?: DegradationCause }
    | { status: 'pending' }
    | {
          status: 'error';
          /** Set when a live request failed: the figure it was replacing, still
           * worth showing beside the error. */
          stale?: KpiStaleCard;
      };

/** A `ready` card's content kept on screen while a re-fetch is in flight or
 * after a live request failed. */
export type KpiStaleCard = {
    data: KpiCardData;
    caveats?: EnvelopeCaveat[];
    computedAt?: string;
    served?: ServedPeriod;
} & Pick<KpiCardDataRequest, 'period' | 'compare'>;

/** Resolved (concrete) period bounds, after applying presets/defaults. */
export interface ResolvedPeriod {
    start: string;
    end: string;
    /** Set when the API should resolve the period itself: the dates above
     * are then only the CMS's reading of it, and are not sent. */
    preset?: PeriodRequest;
}

/** Resolved region: a concrete province list once defaults are applied. */
export type ResolvedRegion = string[];

/** A campaign dashboard's one resolved window, owned by the page and handed to
 * every card on it. Resolved by `useCampaignWindow`. */
export type KpiCampaignContext =
    | { status: 'loading' }
    | {
          status: 'ready';
          toDate: ResolvedPeriod;
          /** The campaign has not ended: today is still one of its days, though
           * `toDate` stops at yesterday. */
          runsToday?: boolean;
      }
    | { status: 'unavailable'; reason: string };

/** What a source needs to produce a card's data. */
export interface KpiCardDataRequest {
    form: KpiForm;
    metric?: KpiMetric;
    period: ResolvedPeriod;
    region: ResolvedRegion;
    /** Resolved compare range, populated upstream (e.g. from a period preset). When present,
     * number/rate sources will include compareStartDate/compareEndDate in the fetch and
     * attach a `trend` to the returned data. */
    compare?: { range: ResolvedPeriod; label: string };
    /** For chart forms: which envelope breakdown axis to render. */
    breakdownBy?: KpiBreakdownAxis;
    /** Carried from `KpiCardSpec.breakdownBaseMetricId` (see there). */
    breakdownBaseMetricId?: KpiMetric;
    /** Carried from `KpiCardSpec.limit`: row count forwarded as the `limit`
     * query param, winning over the source's `staticParams`. */
    limit?: number;
    /** The color ramp passed through to the source/renderer; see `KpiPalette`. */
    palette?: KpiPalette;
    /** For a source that scopes to a single entity (the coupon-activation
     * coupon selection): forwarded as a `promotionId` query param so the endpoint
     * returns that coupon's metrics (and un-suppresses available/redemptionRate).
     * Omitted for every card without a selection, so other cards are unaffected. */
    promotionId?: string;
    /** For a source that reads more than one campaign: forwarded as a
     * `campaignId` query param, winning over the source's `staticParams`.
     * Omitted for every card without one, so other cards are unaffected. */
    campaignId?: string;
    /** For a source that scopes to one customer group (the Milestone rewards
     * group selector): forwarded as a `segment` query param. Omitted for every
     * card without a selection, so other cards are unaffected. */
    segment?: KpiSegment;
    /** The campaign-to-date window (see `KpiCampaignContext`), supplied by the
     * page. A `trio` request without it is a misconfigured campaign and serves
     * no total; every other form ignores it. */
    campaignToDate?: ResolvedPeriod;
    /** See `KpiCampaignContext`. */
    campaignRunsToday?: boolean;
    /** Carried straight from `KpiCardSpec.sample` (see there): an inline sample
     * payload the `placeholder` source returns verbatim for this card instead of
     * its global per-form default. Omitted for every card that doesn't pin one. */
    sample?: KpiCardData;
    /** Asks a live-capable source for a fresh computation instead of the
     * stored figures; forwarded as `live=1`. */
    live?: boolean;
}

/** Source self-description (also powers a future card-builder UI).
 *
 * `M` is the exact set of metric ids THIS source serves, kept as a literal
 * union rather than widened to `string`. That is what lets `card-spec.ts`
 * derive a card's legal metric domain from the source it names. Widening it
 * here (the old `{ id: string }`) silently threw that away and left the
 * card-to-source pairing to a runtime test. */
export interface KpiSourceDescriptor<M extends KpiMetric = KpiMetric> {
    label: string;
    metrics: readonly { id: M; label: string }[];
    forms: KpiForm[];
    /** The finest region grain this source's endpoint actually resolves
     * (mirrors `EnvelopeSourceConfig.regionGrain`). Threaded onto the card's
     * (i) info popover so a businessUnit/brand/national card can surface the
     * honest region-resolution sentence (`grainInfo`); a province source, or
     * a non-envelope source that declares none (e.g. `placeholder`), adds no
     * extra line. Optional so only envelope-backed sources need to carry it. */
    regionGrain?: RegionGrain;
    /** Set when the source draws its `multiline` lines from this named metric
     * list rather than from a breakdown axis, so a card naming `breakdownBy`
     * is naming an axis this source never reads. */
    multilineMetrics?: readonly { label: string; metricId: M }[];
    /** True when the endpoint can compute on request (`live=1`), which the card
     * offers as a "Live data" button. */
    liveCapable?: boolean;
}

/** Dependencies a source needs at fetch time. Stores live in React context, so
 * they are passed in (not imported as a singleton, which this codebase lacks). */
export interface KpiSourceDeps {
    analyticsStore: AnalyticsStore;
    /** The card's per-fetch abort signal. Forwarded to `fetchKpiEnvelope` so a
     * stale request is dropped or aborted when the card re-runs under a new
     * filter, freeing its slot. Optional: sources without it just aren't
     * abandonable. */
    signal?: AbortSignal;
}

/** A data source: describes itself and produces canonical card data. */
export interface KpiSource<M extends KpiMetric = KpiMetric> {
    descriptor: KpiSourceDescriptor<M>;
    /** When true, the source fabricates network-free sample data; the shell renders its cards grayed out. */
    sample?: boolean;
    getCardData(
        req: KpiCardDataRequest,
        deps: KpiSourceDeps
    ): Promise<KpiCardState>;
}

/** Props every form renderer receives (only the ready data; non-ready states are handled by the bridge). */
export interface KpiFormProps {
    data: KpiCardData;
    size: KpiSize;
    /** Lower-is-better metric: render the trend with inverted sentiment (a rise is bad). */
    invertTrend?: boolean;
    /** For `bars` cards in rate mode: drop the per-row base so each row reads just the
     * percentage (the shared base is surfaced in the card title instead). */
    barBaseInHeader?: boolean;
    /** `trio` form only: see `KpiCardSpecBase.toDateOnly`. */
    toDateOnly?: boolean;
    /** `pie` form only: see `KpiCardSpecBase.headlineFromFirstSlice`. */
    headlineFromFirstSlice?: boolean;
    /** `pie` form only: see `KpiCardSpecBase.legendBelow`. */
    legendBelow?: boolean;
    /** `pie` form only: see `KpiCardSpecBase.legendBeside`. */
    legendBeside?: 'compact';
    /** Color ramp for a chart form; see `KpiCardSpecBase.palette`. */
    palette?: KpiPalette;
    /** `bars` form only: see `KpiCardSpecBase.barsStyle`. */
    barsStyle?: 'filled';
    /** `columns` form only: see `KpiCardSpecBase.shareAxis`. */
    shareAxis?: boolean;
    /** `line`/`multiline` form only: see `KpiCardSpecBase.chartStyle`. */
    chartStyle?: 'area';
    /** `columns` form only: see `KpiCardSpecBase.subLabelUnit`. */
    subLabelUnit?: 'days';
    /** `number`/`rate` form only: see `KpiCardSpecBase.boxed`. */
    boxed?: boolean;
}
